// Account-canonical theme (M3 follow-up, 2026-07-03). Theme is cross-device IDENTITY state that
// follows the user (alongside display_name + avatar) — NOT device-local chrome. The source of truth
// is account_profile.theme_preference (DB, per-actor); the @perlab/ui `shell.theme` cookie is a fast
// local CACHE for flash-free first paint. This resolves the half-wired duality the M3 audit flagged:
// previously the account value only synced through the Profile page while the TopBar toggle wrote the
// cookie alone, so the two tiers drifted and a saved theme didn't apply on shell load on a new device.
//
// Two halves:
//  · useThemeAccountSync() — app-root singleton (mounted in AppShell). When the shared profile query
//    resolves, apply the stored theme ONCE per session so a login on ANY surface (not just Profile)
//    picks up the account theme. The cookie paints instantly first; the account reconciles when it
//    arrives. Seeds cookie←account only (raw setTheme) — it must NOT write back (no echo PUT).
//  · useAccountTheme() — the WRITE path. Wraps useTheme so every change (TopBar toggle, Profile select)
//    write-throughs to account_profile via patchProfile, keeping the account value authoritative and
//    never staler than the cookie.
import { useCallback, useEffect, useRef } from "react";
import { useTheme, type Theme } from "@perlab/ui";
import { useProfile } from "../api/useProfile";
import { patchProfile } from "../api/profile";

/** App-root singleton: seed the device cookie from the account theme once the profile loads. */
export function useThemeAccountSync(): void {
  const { theme, setTheme } = useTheme();
  const { data: profile } = useProfile();
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current) return;
    const stored = profile?.theme_preference;
    if (!stored) return;
    applied.current = true;
    if (stored !== theme) setTheme(stored); // cookie←account; raw setTheme = no write-back
  }, [profile, theme, setTheme]);
}

/** Theme accessor whose setter is account-canonical: applies locally AND write-throughs to the DB. */
export function useAccountTheme(): {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (t: Theme) => void;
  toggle: () => void;
} {
  const { theme, resolvedTheme, setTheme: setThemeLocal } = useTheme();
  const setTheme = useCallback(
    (t: Theme) => {
      setThemeLocal(t);
      void patchProfile({ theme_preference: t }); // write-through (best-effort; cookie already applied)
    },
    [setThemeLocal],
  );
  const toggle = useCallback(() => {
    const next: Theme = resolvedTheme === "light" ? "dark" : "light";
    setTheme(next);
  }, [resolvedTheme, setTheme]);
  return { theme, resolvedTheme, setTheme, toggle };
}
